'use client';

import CustomLink from 'components/shared/CustomLink';
import { useSearchParams } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useMemo } from 'react';
import { Container } from '..';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './ProductSection.module.css';

enum Locales {
  INDIA = 'india',
  UAE = 'uae',
  ROW = 'row',
  DEFAULT = 'default',
}

export const ProductSection = ({
  className,
  code,
  ring_active,
  m1_active,
  home_active,
  blood_vision_active,
  ring_diesel_active,
  ring_pro_active,
  all_products_active,
  alternateUI,
  discount_config,
}: {
  className?: string;
  m1_active?: boolean;
  ring_active?: boolean;
  ring_pro_active?: boolean;
  home_active?: boolean;
  blood_vision_active?: boolean;
  ring_diesel_active?: boolean;
  all_products_active?: boolean;
  name?: string;
  percentage_discount?: string | null;
  discount_code?: string | null;
  code?: string;
  alternateUI?: boolean;
  discount_config?:
    | {
        [key in Locales]: {
          code: string;
          type: 'percentage' | 'amount';
          amount: string;
          percentage: string;
        };
      }
    | null;
}) => {
  const { region: country } = useRegionLocale();
  const searchParams = useSearchParams();
  const routerQuery = useMemo(() => {
    const query: Record<string, string | string[]> = {};
    if (!searchParams) return query;
    for (const key of searchParams.keys()) {
      const values = searchParams.getAll(key);
      query[key] = values.length > 1 ? values : values[0];
    }
    return query;
  }, [searchParams]);

  const bentoBase = `${styles['bento-card']} ${styles.light} ${styles.m1}`;

  // Helper function to get discount code based on country and discount_config
  const getDiscountCode = () => {
    if (discount_config && country) {
      // Map ISO country code to locale key
      let localeKey: keyof typeof discount_config = Locales.DEFAULT;
      if (country === 'IN') {
        localeKey = Locales.INDIA;
      } else if (country === 'AE') {
        localeKey = Locales.UAE;
      } else {
        localeKey = Locales.ROW;
      }
      if (discount_config[localeKey]) {
        return discount_config[localeKey].code;
      }
    }
    return undefined;
  };

  // Remove 'token' and the `invite` route param from the incoming query when
  // building href query params
  const getFilteredQuery = () => {
    const { token, discountCode, discount, invite, ...rest } = routerQuery;

    // If we have a discount_config (meaning token is valid), include discount parameter
    // Otherwise, remove discount parameters to prevent expired tokens from being forwarded
    if (discount_config && getDiscountCode()) {
      // All products use 'discount' parameter, we'll add it in the specific product cards
      return rest;
    }

    return rest;
  };

  // All products use 'discount' parameter (Shopify sync)
  const getDiscountParamForProduct = () => {
    return 'discount'; // All products use 'discount' (Shopify sync)
  };

  const m1LinkCard = (
    <CustomLink
      href={{
        pathname: '/pricing',
        query: {
          ...getFilteredQuery(),
          affiliateCode: code,
          ...(getDiscountCode() !== undefined
            ? { [getDiscountParamForProduct()]: getDiscountCode() }
            : {}),
        },
      }}
      className={bentoBase}
      style={{
        backgroundImage: `url(${getCompressedAssetUrl(
          '/web_v2/backgrounds/m1-bento-bg.png/w_1080',
        )})`,
      }}
    >
      {getDiscountCode() !== undefined ? (
        <p className={styles.tag}>15% off</p>
      ) : null}
      <h2>Ultrahuman M1{country == 'AE' ? ' Live' : null}</h2>
      <p>
        Supercharge your nutrition and exercise performance using glucose
        biomarkers.
      </p>
      <button>Buy now</button>
    </CustomLink>
  );

  const ringLinkCard = (
    <CustomLink
      href={{
        pathname: `/ring/buy/`,
        query: {
          ...getFilteredQuery(),
          affiliateCode: code,
          ...(getDiscountCode() !== undefined
            ? { [getDiscountParamForProduct()]: getDiscountCode() }
            : {}),
        },
      }}
      className={bentoBase}
      style={{
        backgroundImage: `url(${getCompressedAssetUrl(
          '/web_v2/backgrounds/ring_invite_bento.png/w_1080',
        )})`,
        backgroundPosition: 'bottom',
        border: `${
          alternateUI ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
        }`,
        overflow: 'hidden',
      }}
    >
      {getDiscountCode() !== undefined ? (
        <p className={styles.tag}>15% off</p>
      ) : null}
      <h2>Ultrahuman Ring AIR</h2>
      <p>
        The world&apos;s most comfortable and compact smart ring that monitors
        your sleep, movement and recovery.
      </p>
      <button>Buy now</button>
    </CustomLink>
  );

  const ringProLinkCard = (
    <CustomLink
      href={{
        pathname: `/ring-pro/buy/`,
        query: {
          ...getFilteredQuery(),
          affiliateCode: code,
          ...(getDiscountCode() !== undefined
            ? { [getDiscountParamForProduct()]: getDiscountCode() }
            : {}),
        },
      }}
      className={bentoBase}
      style={{
        backgroundImage: `url(${getCompressedAssetUrl(
          '/web_v2/backgrounds/ring-pro-3.png/w_1080',
        )})`,
        backgroundPosition: 'bottom',
        border: `${
          alternateUI ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
        }`,
        overflow: 'hidden',
      }}
    >
      {getDiscountCode() !== undefined ? (
        <p className={styles.tag}>15% off</p>
      ) : null}
      <h2>Ultrahuman Ring PRO</h2>
      <p>
        Our most advanced ring yet. A unibody fighter jet-grade titanium build
        — made for serious tracking.
      </p>
      <button>Buy now</button>
    </CustomLink>
  );

  const bloodVisionCard = (
    <CustomLink
      href={{
        pathname: `/blood-vision/buy`,
        query: {
          ...getFilteredQuery(),
          affiliateCode: code,
          ...(getDiscountCode() !== undefined
            ? { [getDiscountParamForProduct()]: getDiscountCode() }
            : {}),
        },
      }}
      className={bentoBase}
      style={{
        backgroundImage: `url(${getCompressedAssetUrl(
          '/web_v2/backgrounds/bv_bento_affiliate.png/w_1080',
        )})`,
        backgroundPosition: 'bottom',
        border: `${
          alternateUI ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
        }`,
        overflow: 'hidden',
      }}
    >
      {getDiscountCode() !== undefined ? (
        <p className={styles.tag}>15% off</p>
      ) : null}
      <h2>Ultrahuman Blood Vision</h2>
      <p>
        A comprehensive blood test to understand your blood markers in the
        context of lifestyle and longevity.
      </p>
      <button>Buy now</button>
    </CustomLink>
  );

  const ringDieselLinkCard = (
    <CustomLink
      href={{
        pathname: `/diesel-ultrahuman-ring/buy/`,
        query: {
          ...getFilteredQuery(),
          affiliateCode: code,
          ...(getDiscountCode() !== undefined
            ? { [getDiscountParamForProduct()]: getDiscountCode() }
            : {}),
        },
      }}
      className={bentoBase}
      style={{
        backgroundImage: `url(${getCompressedAssetUrl(
          '/web_v2/backgrounds/diesel_invite_3.png/w_1080',
        )})`,
        backgroundPosition: 'bottom',
        border: `${
          alternateUI ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
        }`,
        overflow: 'hidden',
      }}
    >
      {getDiscountCode() !== undefined ? (
        <p className={styles.tag}>15% off</p>
      ) : null}
      <h2>Diesel Ultrahuman Ring</h2>
      <p>
        The world’s most comfortable smart ring. Tuned to your energy. Shaped
        by Diesel’s attitude.
      </p>
      <button>Buy now</button>
    </CustomLink>
  );

  // width half of the old `width > 768 && country == 'US'` gate is CSS now (useWindowSize is 0 on SSR); country stays in JS
  const AllProductsCard = (
    <>
      {country == 'US' && (
        <CustomLink
          href={{
            pathname: `/shop`,
            query: {
              ...getFilteredQuery(),
              affiliateCode: code,
              ...(getDiscountCode() !== undefined
                ? { [getDiscountParamForProduct()]: getDiscountCode() }
                : {}),
            },
          }}
          className={`${bentoBase} ${styles['all-products-card']} ${styles['all-products-desktop']}`}
          style={{
            border: `${
              alternateUI ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
            }`,
            overflow: 'hidden',
          }}
        >
          <div className={styles.content}>
            <div className={styles['content-container']}>
              {getDiscountCode() !== undefined ? (
                <p className={styles.tag}>15% off</p>
              ) : null}
              <h2>Ultrahuman Shop</h2>
              <p>Essentials to enhance focus, recovery, and performance.</p>
              <button>Buy now</button>
            </div>
          </div>
          <div
            className={styles['image-container']}
            style={{
              backgroundImage: `url(${getCompressedAssetUrl(
                '/web_v2/backgrounds/store_asset.png/w_1080',
              )})`,
            }}
          ></div>
        </CustomLink>
      )}
      <CustomLink
        href={{
          pathname: `/shop`,
          query: {
            ...getFilteredQuery(),
            affiliateCode: code,
            ...(getDiscountCode() !== undefined
              ? { [getDiscountParamForProduct()]: getDiscountCode() }
              : {}),
          },
        }}
        className={`${bentoBase}${
          country == 'US' ? ` ${styles['all-products-fallback']}` : ''
        }`}
        style={{
          backgroundImage: `url(${getCompressedAssetUrl(
            '/web_v2/backgrounds/store_asset.png/w_1080',
          )})`,
        }}
      >
        {getDiscountCode() !== undefined ? (
          <p className={styles.tag}>15% off</p>
        ) : null}
        <h2>Ultrahuman Shop</h2>
        <p>Your one-stop shop for all things Ultrahuman.</p>
        <button>Buy now</button>
      </CustomLink>
    </>
  );

  const homeCard = (
    <CustomLink
      href={{
        pathname: '/home/buy',
        query: {
          ...getFilteredQuery(),
          affiliateCode: code,
          ...(getDiscountCode() !== undefined
            ? { [getDiscountParamForProduct()]: getDiscountCode() }
            : {}),
        },
      }}
      className={bentoBase}
      style={{
        backgroundImage: `url(${getCompressedAssetUrl(
          '/web_v2/backgrounds/home_invite_bento.png/w_1080',
        )})`,
      }}
    >
      {getDiscountCode() !== undefined ? (
        <p className={styles.tag}>15% off</p>
      ) : null}
      <h2>Ultrahuman Home</h2>
      <p>A quiet yet powerful sleep monitor to help you rest better.</p>
      <button>Buy now</button>
    </CustomLink>
  );

  return (
    <section
      className={`${styles.root}${className ? ` ${className}` : ''}`}
      style={{ backgroundColor: alternateUI ? '#000' : '#eeeeee' }}
    >
      {alternateUI && (
        <h2 className={styles['corporate-head']}>Get special prices</h2>
      )}

      <Container
        className={
          [
            ring_active === true && country !== 'US',
            ring_pro_active === true,
            m1_active === true,
            home_active === true,
            blood_vision_active === true,
          ].filter(Boolean).length === 1
            ? styles['single-card']
            : ''
        }
      >
        {ring_active === true && country !== 'US' && ringLinkCard}
        {ring_pro_active === true && ringProLinkCard}
        {ring_diesel_active === true && ringDieselLinkCard}

        {home_active === true && homeCard}

        {blood_vision_active === true && bloodVisionCard}

        {m1_active === true && m1LinkCard}

        {m1_active === undefined &&
          ring_active === undefined &&
          blood_vision_active === undefined &&
          home_active === undefined && (
            <>
              {ringLinkCard}
              {m1LinkCard}
            </>
          )}
        {all_products_active === true && AllProductsCard}
      </Container>
    </section>
  );
};
